import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import { Box, Divider } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useDispatch, useSelector } from "react-redux";
import CrossIcon from "../../../assets/Icons/CrossIcon";
import theme from "../../../theme";
import CustomButton from "../../common/button/CustomButton";
import OrderDeliveryAddressForm from "../../order/OrderDeliveryAddressForm";
import { GET_ADDRESS_LIST } from "../../../actions/AddressActions";
import {
  cloneAddressSnapshot,
  formatAddressLine,
} from "../../../utils/orderAddress";
import {
  StyledAddressLabel,
  StyledAddressLine,
  StyledAddressSection,
  StyledAddressSectionHeader,
  StyledConfirmText,
  StyledDefaultBadge,
  StyledEditLink,
  StyledModalBody,
  StyledModalBox,
  StyledModalFooter,
  StyledModalHeader,
  StyledModalTitle,
} from "./style";

export default function ConfirmCreateOrder({ open, onClose, onConfirm }) {
  const dispatch = useDispatch();
  const addressList = useSelector((state) => state?.address?.addressList);
  const isCreating = useSelector((state) => state?.order?.isLoading);
  const [editingAddress, setEditingAddress] = useState(false);
  const [orderAddress, setOrderAddress] = useState(null);

  const profileDefaultAddress = useMemo(() => {
    if (!addressList?.length) return null;
    return addressList.find((a) => a.is_default) || addressList[0];
  }, [addressList]);

  const refreshAddresses = useCallback(() => {
    dispatch({ type: GET_ADDRESS_LIST });
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    refreshAddresses();
    setEditingAddress(false);
  }, [open, refreshAddresses]);

  useEffect(() => {
    if (!open) return;
    if (profileDefaultAddress) {
      setOrderAddress(cloneAddressSnapshot(profileDefaultAddress));
    } else {
      setOrderAddress(null);
    }
  }, [open, profileDefaultAddress]);

  const handleConfirm = () => {
    if (!orderAddress || isCreating) return;
    onConfirm(orderAddress);
  };

  const handleSaveOrderAddress = (values) => {
    setOrderAddress(values);
    setEditingAddress(false);
  };

  return (
    <Modal open={open} onClose={isCreating ? undefined : onClose}>
      <StyledModalBox>
        <StyledModalHeader>
          <StyledModalTitle>Confirm create order</StyledModalTitle>
          <CrossIcon
            color={theme.palette.custom.white}
            onClick={isCreating ? undefined : onClose}
          />
        </StyledModalHeader>

        <StyledModalBody>
          <StyledConfirmText>
            Are you sure you want to create a new order?
          </StyledConfirmText>

          <Divider sx={{ my: 2 }} />

          <StyledAddressSection>
            <StyledAddressSectionHeader>
              <StyledAddressLabel>Order delivery address</StyledAddressLabel>
              {orderAddress && !editingAddress && (
                <StyledEditLink
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={() => setEditingAddress(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setEditingAddress(true);
                  }}
                >
                  Edit
                </StyledEditLink>
              )}
              {editingAddress && (
                <StyledEditLink
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={() => setEditingAddress(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setEditingAddress(false);
                  }}
                >
                  Cancel edit
                </StyledEditLink>
              )}
            </StyledAddressSectionHeader>

            {editingAddress && orderAddress ? (
              <OrderDeliveryAddressForm
                embedded
                initialValues={orderAddress}
                submitLabel="Use this address"
                onSave={handleSaveOrderAddress}
                onCancel={() => setEditingAddress(false)}
              />
            ) : orderAddress ? (
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <LocationOnOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
                <Box>
                  {profileDefaultAddress?.is_default && (
                    <StyledDefaultBadge>
                      Based on your profile default
                    </StyledDefaultBadge>
                  )}
                  <StyledAddressLine sx={{ textTransform: "capitalize", mb: 0.5 }}>
                    {orderAddress.address_type || "home"}
                  </StyledAddressLine>
                  <StyledAddressLine>{formatAddressLine(orderAddress)}</StyledAddressLine>
                </Box>
              </Box>
            ) : (
              <StyledAddressLine>
                No delivery address found. Add a default address in your profile
                first, or edit here after adding one in Profile.
              </StyledAddressLine>
            )}
          </StyledAddressSection>
        </StyledModalBody>

        <StyledModalFooter>
          <CustomButton
            padding="10px 24px"
            fontSize={theme.typography.regular.fontSize}
            height="44px"
            btnLabel="Cancel"
            btntype="white"
            color={theme.palette.custom.lockerValueColor}
            fontWeight={theme.typography.fontWeight.medium}
            border={theme.borders.defaultButton}
            onClick={onClose}
            disabled={isCreating}
          />
          <CustomButton
            padding="10px 28px"
            fontSize={theme.typography.regular.fontSize}
            height="44px"
            btnLabel="Create order"
            btntype="primary"
            color={theme.palette.custom.white}
            fontWeight={theme.typography.fontWeight.medium}
            onClick={handleConfirm}
            isLoading={isCreating}
            disabled={!orderAddress || isCreating || editingAddress}
          />
        </StyledModalFooter>
      </StyledModalBox>
    </Modal>
  );
}
